// Global keyboard shortcuts for the Dashboard, plus the list the "?" dialog
// shows. The handler is pure of React so it can be tested directly
// (shortcuts.test.mjs); Dashboard wires it to a window keydown listener.

export type Shortcut = { keys: string[]; description: string };

// Shown by ShortcutsHelp. Keep in step with handleGlobalKeydown and the form /
// dialog behaviour it describes.
export const SHORTCUTS: Shortcut[] = [
  { keys: ["/"], description: "Focus the search box (not in the Admin view)" },
  { keys: ["Esc"], description: "Clear the search box while it's focused; close a menu or dialog" },
  { keys: ["Enter"], description: "Add what you typed, or save a rename" },
  { keys: ["Tab", "Shift+Tab"], description: "Move between controls (stays inside an open dialog)" },
  { keys: ["?"], description: "Show this list" },
];

type GlobalKeyEvent = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "altKey" | "isComposing" | "preventDefault"
> & { target: EventTarget | null };

export type GlobalKeyActions = {
  // True while the todo view is hidden behind the Admin tab; "/" has nothing to focus there.
  showAdmin: boolean;
  // True while a modal dialog or a focus-trapping popover is open; "?" must not
  // stack a second dialog on top (see isDialogOpen).
  modalOpen: () => boolean;
  searchFocused: () => boolean;
  focusSearch: () => void;
  clearSearch: () => void;
  openShortcuts: () => void;
};

// Keys typed into a field belong to the field. isContentEditable covers rich-text
// hosts (it's undefined on non-HTML targets such as SVG, document or window).
export const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  const tag = el?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable === true;
};

// Something that traps focus is already up: a modal (aria-modal) or a popover
// whose trigger reports it expanded (HeaderSettings, TodoMenu). Popovers are
// detected via their trigger rather than role="dialog" because HeaderSettings'
// panel stays mounted (hidden) while closed. Opening ShortcutsHelp over one
// would run two useFocusTrap listeners, so a single Escape closed both.
export const OPEN_DIALOG_SELECTOR = '[aria-modal="true"], [aria-haspopup][aria-expanded="true"]';

export const isDialogOpen = (doc: Pick<Document, "querySelector">): boolean =>
  doc.querySelector(OPEN_DIALOG_SELECTOR) !== null;

export const handleGlobalKeydown = (e: GlobalKeyEvent, a: GlobalKeyActions): void => {
  if (e.key === "Escape") {
    if (a.searchFocused()) a.clearSearch();
    return;
  }
  // Leave browser and OS combos (Cmd+/, Alt+?) and IME composition alone.
  if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
  if (isTypingTarget(e.target)) return;
  if (e.key === "/") {
    if (a.showAdmin) return;
    e.preventDefault();
    a.focusSearch();
  } else if (e.key === "?") {
    if (a.modalOpen()) return;
    e.preventDefault();
    a.openShortcuts();
  }
};
